import { FileAttachment02, Trash03, UploadCloud01 } from "untitledui-js-base";
import { cx } from "shared/utils";
import { IconButton, IconButtonSize } from "shared/components";

import { useCallback, useRef, MutableRefObject } from "react";
import { toast } from "react-toastify";

const Preview = (props: { file: File; onDelete: () => void }) => {
  const { name } = props.file;
  return (
    <div className="my-4 flex items-center justify-between rounded-lg border border-alpha-100 bg-alpha-40 p-4">
      <div className="flex items-center gap-3">
        <FileAttachment02 className="stroke-content-300" size="24" />
        <div className="w-full">
          <p className="truncate max-w-[400px] text-sm font-bold text-content-1000">
            {name}
          </p>
          <p className="text-xs text-content-200">Uploaded</p>
        </div>
      </div>
      <IconButton
        Icon={Trash03}
        className="h-10 w-10 stroke-danger-500"
        onClick={() => {
          props.onDelete();
        }}
        size={IconButtonSize.SM}
      />
    </div>
  );
};

/**
 *  The `FileWidget` is a widget for rendering file upload fields.
 *  It is typically used with a string property with data-url format.
 */
export const DropZone = ({
  id,
  title,
  description,
  value,
  setValue
}: {
  id: string;
  title?: string;
  description?: string;
  value?: File;
  setValue: (value?: File) => void;
}) => {
  const uploader = useRef<HTMLInputElement>();

  const handleChange = useCallback(
    (e) => {
      if (e.target.files && e.target.files.length > 0)
        if (e.target.files[0].size > 2097152)
          toast.error("File size should be less than 2MB");
        else setValue(e.target.files[0]);
      else setValue(undefined);
    },
    [setValue]
  );

  return (
    <div>
      {value ? (
        <Preview
          file={value}
          onDelete={() => {
            if (uploader.current?.value) uploader.current.value = "";

            setValue(undefined);
          }}
        />
      ) : null}
      <label
        className={cx(
          "animate-appear relative flex h-[140px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-primary-500 bg-primary-20 hover:bg-primary-50",
          value && "hidden"
        )}
        htmlFor={id}
      >
        <div className="flex flex-col items-center justify-center gap-2 pb-6 pt-5">
          <UploadCloud01 className="stroke-primary-500 stroke-2" size="24" />
          <p className="text-sm font-bold text-primary-500">
            {title || "Upload files"}
          </p>
          {description ? (
            <p className="text-xs font-medium text-primary-500">
              {description}
            </p>
          ) : null}
        </div>

        <input
          ref={uploader as MutableRefObject<HTMLInputElement>}
          accept=".csv"
          className="absolute left-0 h-[140px] w-full opacity-0"
          id={id}
          multiple={false}
          onChange={handleChange}
          required={false}
          type="file"
        />
      </label>
    </div>
  );
};
